import { useEffect, useRef, useState } from "react";
import { useCollabSession, type CollabUser } from "./CollabSessionContext";
import { ACTIVITY_AWARENESS_FIELD, parseActivityAwareness, type ActivityAwarenessV1 } from "./activityAwareness";
import type { TrustedPresenceBinding } from "./trustedPresenceBindings";

export interface PeerState {
    clientId: number;
    user?: CollabUser;
    activity?: ActivityAwarenessV1;
    sectionField?: string;
    pointer?: { xf: number; yf: number; sectionField: string; t: number };
}

export interface UsePresenceOptions {
    includePointers?: boolean;
}

/** Header identity is deduplicated by principal across same-user tabs. */
export function dedupePeersByUser(peers: PeerState[]): PeerState[] {
    const byUser = new Map<number, PeerState>();
    for (const peer of peers) {
        if (!peer.user) continue;
        const existing = byUser.get(peer.user.userid);
        if (!existing || peer.clientId < existing.clientId) byUser.set(peer.user.userid, peer);
    }
    return [...byUser.values()];
}

/** Resource indicators retain one entry per principal and typed target. */
export function dedupePeersByUserAndTarget(peers: PeerState[]): PeerState[] {
    const result = new Map<string, PeerState>();
    for (const peer of peers) {
        if (!peer.user || !peer.activity) continue;
        const key = `${peer.user.userid}:${peer.activity.target.kind}:${peer.activity.target.id}`;
        const existing = result.get(key);
        if (!existing || peer.clientId < existing.clientId) result.set(key, peer);
    }
    return [...result.values()];
}

export function identitySignature(peers: PeerState[]): string {
    return peers.map(peer => [
        peer.clientId,
        peer.user?.userid ?? "",
        peer.user?.name ?? "",
        peer.user?.color ?? "",
        peer.activity?.target.kind ?? "",
        peer.activity?.target.id ?? "",
        peer.activity?.activity ?? "",
        peer.sectionField ?? ""
    ].join("|")).join("§");
}

export function peerFromAwareness(
    clientId: number,
    state: Record<string, any>,
    bindings: ReadonlyMap<number, TrustedPresenceBinding>,
    now: number,
    includePointers = false
): PeerState {
    const activity = parseActivityAwareness(state[ACTIVITY_AWARENESS_FIELD], now) ?? undefined;
    const binding = bindings.get(clientId);
    return {
        clientId,
        user: binding && binding.expiresAt > now ? binding.user : undefined,
        activity,
        sectionField: activity?.target.kind === "section" ? `section-${activity.target.id}` : undefined,
        pointer: includePointers ? state.pointer : undefined
    };
}

export function usePresence(options?: UsePresenceOptions): PeerState[] {
    const includePointers = options?.includePointers ?? false;
    const session = useCollabSession();
    const [peers, setPeers] = useState<PeerState[]>([]);
    const lastSignatureRef = useRef<string | null>(null);

    useEffect(() => {
        const awareness = session?.provider.awareness;
        if (!awareness) {
            setPeers([]);
            lastSignatureRef.current = null;
            return;
        }
        const readStates = () => {
            const now = Date.now();
            const next: PeerState[] = [];
            awareness.getStates().forEach((state: Record<string, any>, clientId: number) => {
                if (clientId === awareness.clientID) return;
                next.push(peerFromAwareness(clientId, state, session.trustedBindings, now, includePointers));
            });
            if (!includePointers) {
                const signature = identitySignature(next);
                if (signature === lastSignatureRef.current) return;
                lastSignatureRef.current = signature;
            }
            setPeers(next);
        };
        readStates();
        awareness.on("update", readStates);
        const expiryTimer = window.setInterval(readStates, 1_000);
        return () => {
            awareness.off("update", readStates);
            window.clearInterval(expiryTimer);
        };
    }, [session, includePointers]);

    return peers;
}
